// pages/ReservasEstados.js
import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Divider,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  BlockOutlined,
  UnlockOutlined,
  SearchOutlined,
  ReloadOutlined,
  EyeOutlined,
  ExportOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  SyncOutlined,
  FileAddOutlined,
  StarOutlined,
  BookOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;

const ReservasEstados = () => {
  // Estados
  const [reservasEstados, setReservasEstados] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [globalSearchText, setGlobalSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentEstado, setCurrentEstado] = useState(null);
  const [form] = Form.useForm();
  const [tableLoading, setTableLoading] = useState(false);
  
  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Cargar datos iniciales
  useEffect(() => {
    fetchReservasEstados();
  }, []);

  const fetchReservasEstados = async () => {
    setTableLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/reservas-estados`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Asegurar que todos los registros tengan campo Activo
      const data = response.data.data.map(item => ({
        ...item,
        Activo: item.Activo === true || item.Activo === 1 || item.Activo === '1'
      }));
      
      setReservasEstados(data);
      setFilteredData(data);
      
      console.log('✅ Estados de reserva cargados:', data);
    } catch (error) {
      message.error('Error al cargar los estados de reserva');
      console.error('Error fetchReservasEstados:', error);
    } finally {
      setTableLoading(false);
    }
  };

  // Función para búsqueda global
  const handleGlobalSearch = (searchText) => {
    setGlobalSearchText(searchText);
    
    if (!searchText.trim()) {
      setFilteredData(reservasEstados);
      return;
    }
    
    const lowerSearchText = searchText.toLowerCase().trim();
    
    const filtered = reservasEstados.filter(record => {
      return Object.keys(record).some(key => {
        const value = record[key];
        
        if (value === null || value === undefined) return false;
        
        const stringValue = String(value).toLowerCase();
        return stringValue.includes(lowerSearchText);
      });
    });
    
    setFilteredData(filtered);
  };

  // Función para obtener el icono según el estado
  const getIconForEstado = (codigo, descripcion) => {
    const descLower = descripcion.toLowerCase();
    
    switch(codigo) {
      case 0: // Sin Estado
        return <ClockCircleOutlined style={{ color: '#8c8c8c' }} />;
      case 1: // Confirmada
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 2: // Revisada
        return <EyeOutlined style={{ color: '#1890ff' }} />;
      case 3: // Nueva
        return <FileAddOutlined style={{ color: '#faad14' }} />;
      case 4: // Opcionada
        return <StarOutlined style={{ color: '#722ed1' }} />;
      default:
        if (descLower.includes('confirmada')) {
          return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        } else if (descLower.includes('revisada')) {
          return <EyeOutlined style={{ color: '#1890ff' }} />;
        } else if (descLower.includes('nueva')) {
          return <FileAddOutlined style={{ color: '#faad14' }} />;
        } else if (descLower.includes('opcionada')) {
          return <StarOutlined style={{ color: '#722ed1' }} />;
        }
        return <BookOutlined style={{ color: '#8c8c8c' }} />;
    }
  };

  // Función para obtener el color del tag según el código
  const getColorForEstado = (codigo) => {
    switch(codigo) {
      case 0: return 'default';
      case 1: return 'success';
      case 2: return 'blue';
      case 3: return 'orange';
      case 4: return 'purple';
      default: return 'default';
    }
  };

  // Configuración de columnas
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 80,
      sorter: (a, b) => a.Codigo - b.Codigo,
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => <Tag color={getColorForEstado(codigo)}>#{codigo}</Tag>,
    },
    {
      title: 'Icono',
      key: 'icono',
      width: 60,
      render: (_, record) => getIconForEstado(record.Codigo, record.Descripcion),
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 200,
      sorter: (a, b) => a.Descripcion.localeCompare(b.Descripcion),
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      ellipsis: true,
    },
    {
      title: 'Estado',
      dataIndex: 'Activo',
      key: 'Activo',
      width: 100,
      render: (activo) => (
        <Badge
          status={activo ? 'success' : 'error'}
          text={activo ? 'Activo' : 'Inactivo'}
        />
      ),
      filters: [
        { text: 'Activos', value: true },
        { text: 'Inactivos', value: false },
      ],
      filteredValue: filteredInfo.Activo || null,
      onFilter: (value, record) => record.Activo === value,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          {/* <Tooltip title={record.Activo ? "Desactivar" : "Activar"}>
            <Button
              type={record.Activo ? "dashed" : "primary"}
              danger={record.Activo}
              icon={record.Activo ? <BlockOutlined /> : <UnlockOutlined />}
              size="small"
              onClick={() => handleToggleActive(record)}
            />
          </Tooltip> */}
          
          <Popconfirm
            title="¿Eliminar estado de reserva?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
          
          <Tooltip title="Ver detalle">
            <Button
              type="text"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Handlers para las acciones
  const handleCreate = () => {
    setModalType('create');
    setCurrentEstado(null);
    form.resetFields();
    form.setFieldsValue({
      Activo: true,
      Descripcion: ''
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentEstado(record);
    
    form.setFieldsValue({
      ...record,
      Activo: record.Activo || false,
      Descripcion: record.Descripcion || ''
    });
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Detalle de Estado de Reserva: ${record.Descripcion}`,
      width: 600,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <strong>Código:</strong> <Tag color={getColorForEstado(record.Codigo)}>#{record.Codigo}</Tag>
            </Col>
            <Col span={8}>
              <strong>Estado:</strong> {record.Activo ? 'ACTIVO' : 'INACTIVO'}
            </Col>
            <Col span={8}>
              <strong>Icono:</strong> {getIconForEstado(record.Codigo, record.Descripcion)}
            </Col>
            <Col span={24}>
              <Divider orientation="left">Descripción</Divider>
            </Col>
            <Col span={24}>
              <Card size="small">
                <Text>{record.Descripcion}</Text>
              </Card>
            </Col>
            <Col span={24}>
              <Divider orientation="left">Información Adicional</Divider>
            </Col>
            <Col span={24}>
              <Text type="secondary">
                {getUsageDescription(record.Codigo, record.Descripcion)}
              </Text>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const getUsageDescription = (codigo, descripcion) => {
    const descLower = descripcion.toLowerCase();
    
    switch(codigo) {
      case 0:
        return 'Estado inicial de las reservas cuando no se ha asignado un estado específico.';
      case 1:
        return 'Reserva confirmada por el cliente y lista para procesar.';
      case 2:
        return 'Reserva revisada por el personal administrativo.';
      case 3:
        return 'Reserva nueva que requiere atención inmediata.';
      case 4:
        return 'Reserva opcionada, en espera de confirmación del cliente.';
      default:
        return 'Estado de reserva general para el sistema de gestión hotelera.';
    }
  };

  const handleToggleActive = async (record) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      const nuevoEstado = !record.Activo;
      
      const response = await axios.patch(
        `${BASE_URL}/api/reservas-estados/${record.Codigo}/estado`,
        { Activo: nuevoEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const estadoActualizado = {
        ...response.data.data,
        Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
      };
      
      setReservasEstados(reservasEstados.map(t => 
        t.Codigo === record.Codigo ? estadoActualizado : t
      ));
      
      setFilteredData(prev => prev.map(t => 
        t.Codigo === record.Codigo ? estadoActualizado : t
      ));
      
      message.success(`Estado de reserva ${nuevoEstado ? 'activado' : 'desactivado'} correctamente`);
      
    } catch (error) {
      message.error('Error al actualizar el estado');
      console.error('Error handleToggleActive:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleDelete = async (codigo) => {
    try {
      setTableLoading(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${BASE_URL}/api/reservas-estados/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const nuevosEstados = reservasEstados.filter(t => t.Codigo !== codigo);
      setReservasEstados(nuevosEstados);
      
      const nuevoFilteredData = filteredData.filter(t => t.Codigo !== codigo);
      setFilteredData(nuevoFilteredData);
      
      message.success('Estado de reserva eliminado correctamente');
      
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar el estado de reserva');
      console.error('Error handleDelete:', error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      console.log('📝 Datos a guardar:', values);
      
      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/reservas-estados`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const nuevoEstado = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
        };
        
        setReservasEstados([...reservasEstados, nuevoEstado]);
        
        if (globalSearchText) {
          const lowerSearchText = globalSearchText.toLowerCase();
          const shouldShowInSearch = Object.keys(nuevoEstado).some(key => {
            const value = nuevoEstado[key];
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(lowerSearchText);
          });
          
          if (shouldShowInSearch) {
            setFilteredData([...filteredData, nuevoEstado]);
          }
        } else {
          setFilteredData([...filteredData, nuevoEstado]);
        }
        
        message.success('Estado de reserva creado correctamente');
        
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/reservas-estados/${currentEstado.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const estadoActualizado = {
          ...response.data.data,
          Activo: response.data.data.Activo === true || response.data.data.Activo === 1 || response.data.data.Activo === '1'
        };
        
        const nuevosEstados = reservasEstados.map(t => 
          t.Codigo === currentEstado.Codigo ? estadoActualizado : t
        );
        
        setReservasEstados(nuevosEstados);
        
        const nuevoFilteredData = filteredData.map(t => 
          t.Codigo === currentEstado.Codigo ? estadoActualizado : t
        );
        
        setFilteredData(nuevoFilteredData);
        
        message.success('Estado de reserva actualizado correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error en handleSave:', error);
      
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else if (error.response?.data?.details) {
        message.error(error.response.data.details);
      } else {
        message.error('Error al guardar los datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
    
    let result = [...reservasEstados];
    
    Object.keys(filters).forEach(key => {
      const filterValues = filters[key];
      if (filterValues && filterValues.length > 0) {
        result = result.filter(item => {
          const itemValue = item[key];
          
          if (key === 'Activo') {
            return filterValues.includes(itemValue);
          }
          
          return true;
        });
      }
    });
    
    if (sorter.field && sorter.order) {
      result.sort((a, b) => {
        const aVal = a[sorter.field] || '';
        const bVal = b[sorter.field] || '';
        
        if (typeof aVal === 'string' && typeof bVal === 'string') {
          if (sorter.order === 'ascend') {
            return aVal.localeCompare(bVal);
          } else {
            return bVal.localeCompare(aVal);
          }
        } else {
          if (sorter.order === 'ascend') {
            return (aVal || 0) - (bVal || 0);
          } else {
            return (bVal || 0) - (aVal || 0);
          }
        }
      });
    }
    
    if (globalSearchText) {
      const lowerSearchText = globalSearchText.toLowerCase().trim();
      result = result.filter(record => {
        return Object.keys(record).some(key => {
          const value = record[key];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(lowerSearchText);
        });
      });
    }
    
    setFilteredData(result);
  };

  const clearFilters = () => {
    setFilteredInfo({});
    setSortedInfo({});
    setGlobalSearchText('');
    setFilteredData(reservasEstados);
    message.success('Filtros y búsqueda limpiados');
  };

  // Función para exportar
  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Estados" 
          value={reservasEstados.length}
          prefix={<BookOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activos" 
          value={reservasEstados.filter(t => t.Activo).length}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Confirmadas" 
          value={reservasEstados.filter(t => 
            t.Descripcion.toLowerCase().includes('confirmada')
          ).length}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Opcionadas" 
          value={reservasEstados.filter(t => 
            t.Descripcion.toLowerCase().includes('opcionada')
          ).length}
          prefix={<StarOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col>
    </Row>
  );

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input.Search
          placeholder="Buscar por código, descripción..."
          value={globalSearchText}
          style={{ width: 400 }}
          allowClear
          enterButton
          onChange={(e) => {
            setGlobalSearchText(e.target.value);
            if (e.target.value === '') {
              handleGlobalSearch('');
            }
          }}
          onSearch={handleGlobalSearch}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={clearFilters}
          size="small"
        >
          Limpiar
        </Button>
        <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
          <strong>{filteredData.length}</strong> registros
        </Tag>
      </Space>
    </div>
  );

  return (
    <>
      <PageContainer
        title="Gestión de Estados de Reserva"
        subtitle={`${reservasEstados.length} estados registrados`}
        icon={<BookOutlined />}
        loading={tableLoading}
        onRefresh={fetchReservasEstados}
        onCreate={handleCreate}
        onExport={handleExport}
        badgeCount={reservasEstados.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{reservasEstados.length}</strong> estados
            </Tag>
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}

        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="Codigo"
          loading={tableLoading}
          onChange={handleChange}
          scroll={{ x: 1200 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar */}
      <Modal
        title={modalType === 'create' ? 'Nuevo Estado de Reserva' : 'Editar Estado de Reserva'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[{ required: true, message: 'Por favor ingrese la descripción' }]}
                help="Ejemplos: Confirmada, Revisada, Nueva, Opcionada"
              >
                <Input 
                  placeholder="Descripción del estado de reserva" 
                  maxLength={100} 
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="Activo"
                label="Estado"
                valuePropName="checked"
              >
                <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6 }}>
            <Text type="secondary">
              <strong>💡 Información:</strong> Los estados de reserva se utilizan para 
              clasificar el estado de las reservas en el sistema (confirmada, pendiente, etc.).
            </Text>
          </div>
        </Form>
      </Modal>
    </>
  );
};

export default ReservasEstados;